import React, { useState } from 'react'
import { ListItemText, ListItem, ListItemIcon } from '@material-ui/core'
import styled from 'styled-components'
import useHover from 'hooks/useHover'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Bell, Lock, IconColors } from 'sputnik-icons'
import { Button, Typography } from 'sputnik-ui'
import { ReactComponent as SomeFailedIcon } from 'assets/loader_firework.svg'
import { ReactComponent as FinishedIcon } from 'assets/checkmark_outline.svg'
import { ReactComponent as DownloadIcon } from 'assets/download_cloud.svg'
import { ReactComponent as FailedIcon } from 'assets/cross.svg'
import {
  startEmergency,
  exportEmergency,
  stopEmergency,
} from 'store/operations'
import SendReport from 'components/atoms/SendReport'

const images = {
  call: <Bell color={IconColors.grey} />,
  unlock: <Lock color={IconColors.grey} />,
}

const BlueText = styled.span`
  color: ${(p) => p.theme.palette.blue.main};
  cursor: pointer;
  margin-left: auto;
  padding-right: 16px;
`

const EmergencyItemContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const SecondaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  float: right;
`

const StyledFinishedIcon = styled(FinishedIcon)`
  color: ${(p) => p.theme.palette.green.main};
  font-size: 1rem;
`
const StyledSomeFailedIcon = styled(FinishedIcon)`
  color: ${(p) => p.theme.palette.green.main};
  font-size: 1rem;
`
const StyledStartedIcon = styled(SomeFailedIcon)`
  color: ${(p) => p.theme.palette.green.grey};
  font-size: 1rem;
`
const StyledFailedIcon = styled(FailedIcon)`
  color: ${(p) => p.theme.palette.error['500']};
  font-size: 1rem;
`

const getStatusIcon = (emergency) => {
  switch (emergency.state) {
    case 'started':
      return <StyledStartedIcon />
    case 'succeeded':
      return <StyledFinishedIcon />
    case 'failed':
      return <StyledFailedIcon />
    case 'some_failed':
      return <StyledSomeFailedIcon />
    default:
      return null
  }
}

const IntercomCalcDisplay = ({ emergency, t }) => {
  if (emergency.state === 'succeeded' || emergency.state === 'some_failed') {
    return (
      <Typography variant="body3">
        {emergency.successful_intercom_count} / {emergency.intercom_count}{' '}
        {t(`emergency:item:intercoms_${emergency.kind}`)}
      </Typography>
    )
  }

  if (emergency.state === 'pending') {
    return (
      <Typography variant="body3">
        {emergency.intercom_count} {t('emergency:item:intercoms_choosen')}
      </Typography>
    )
  }

  return <></>
}
const ActionButton = ({ emergency, dispatch, t }) => {
  const buttonText = (kind) => {
    if (kind === 'unlock') {
      return t('common:button:open_doors')
    }
    return t('common:button:launch')
  }

  const buttonStyle = {
    width: '180px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
  if (emergency.state === 'pending') {
    return (
      <Button
        primary
        style={buttonStyle}
        onClick={() => {
          dispatch(startEmergency(emergency.id))
        }}
      >
        {' '}
        {buttonText(emergency.kind)}{' '}
      </Button>
    )
  }

  if (emergency.state === 'started') {
    return (
      <Button
        secondary
        style={buttonStyle}
        onClick={() => {
          dispatch(stopEmergency(emergency.id))
        }}
      >
        {emergency.kind === 'unlock'
          ? t('emergency:button:close_doors')
          : t('common:button:stop')}
      </Button>
    )
  }

  return <></>
}

const EmergencyItem = ({ emergency, key, handleOpen, currentUser }) => {
  const { hovered, bind } = useHover()
  const dispatch = useDispatch()
  const { t } = useTranslation('common', 'emergency')

  const [exportEmailEmergency, setExportEmailEmergency] = useState(false)

  const parseCreatedDuration = (datetime) => {
    const parsed = new Date(datetime)
    const formattedDate = parsed
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(/\//g, '.')
    return formattedDate
  }

  return (
    <EmergencyItemContainer>
      <ListItem style={{ width: '100%' }} key={key} {...bind}>
        <ListItemIcon>{images[emergency.kind]}</ListItemIcon>
        <ListItemText
          primary={
            <>
              <Typography variant="h6" style={{ fontWeight: 400 }}>
                {emergency.title}
              </Typography>
              <IntercomCalcDisplay emergency={emergency} t={t} />
            </>
          }
          secondary={
            <>
              <div style={{ gap: '30px', display: 'flex' }}>
                <Typography variant="body3" color="textSecondary">
                  {t('emergency:item:created_at')} :{' '}
                  {parseCreatedDuration(emergency.created_at)}
                </Typography>
                {emergency.succeeded_at && (
                  <Typography variant="body3" color="textSecondary">
                    {t('emergency:item:succeeded_at')} :{' '}
                    {parseCreatedDuration(emergency.succeeded_at)}
                  </Typography>
                )}

                <Typography variant="body3" color="textSecondary">
                  {t('common:duration')}: {emergency.duration / 60}{' '}
                  {t('common:minute')}
                </Typography>
              </div>
            </>
          }
        />
      </ListItem>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '30px',
        }}
      >
        <SecondaryContent>
          {getStatusIcon(emergency)}
          <Typography variant="body2" color="textSecondary">
            {t(`emergency:state:${emergency.state}`)}
          </Typography>
          <ActionButton emergency={emergency} dispatch={dispatch} t={t} />
          <DownloadIcon color={IconColors.blue} />
          <BlueText onClick={() => setExportEmailEmergency(true)}>
            {t('common:button:download_report')}
          </BlueText>
        </SecondaryContent>
      </div>
      <SendReport
        open={exportEmailEmergency}
        handleClose={() => setExportEmailEmergency(false)}
        defaultEmail={currentUser.email}
        emailLabel={t('emergency:export.info')}
        operation={(email) => exportEmergency(emergency.id, email)}
      />
    </EmergencyItemContainer>
  )
}

export default EmergencyItem
